import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { IconButton, Tooltip } from '@mui/material';
import JsonSyntaxHighlighter from './sandbox/JsonSyntaxHighlighter';

// standard HTTP reason phrases for the statuses Trackstar emits
const STATUS_REASON = {
  200: 'OK',
  201: 'Created',
  204: 'No Content',
  301: 'Moved Permanently',
  302: 'Found',
  304: 'Not Modified',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  409: 'Conflict',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

const getStatusReasonPhrase = (code) => STATUS_REASON[code] || '';

function EmptyBodyInline({ text }) {
  return <span className="text-[11px] text-surface-400 italic">{text}</span>;
}

// shown when the request body was intentionally not captured (e.g. the
// integration keeps request bodies out of our logs for security). Friendlier
// than a bare "No request body" so customers know it's by design, not a bug.
function SecurityRedactedBody({ integrationName }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-8 px-4 gap-2">
      <div className="flex items-center justify-center w-9 h-9 rounded-full bg-surface-100">
        <LockOutlinedIcon sx={{ fontSize: '18px', color: '#78716C' }} />
      </div>
      <span className="text-sm font-medium text-surface-700">
        Request body not logged
      </span>
      <span className="text-xs text-surface-400 max-w-xs">
        {integrationName && (
          <>{integrationName} requires sensitive information in the API call.{' '}</>
        )}
        For security reasons, we couldn&rsquo;t log a body for this request.
      </span>
    </div>
  );
}

function CodePanel({
  title, subtitle, accent = 'neutral', leading, toolbar, children, bodyClassName = '',
}) {
  const isError = accent === 'error';
  return (
    <section className={`flex-1 flex flex-col min-h-0 rounded-lg border overflow-hidden ${isError ? 'border-red-200' : 'border-surface-200'}`}>
      <div className={`flex items-center gap-2 px-3 py-2 border-b ${isError ? 'bg-error-light border-red-200' : 'bg-surface-50 border-surface-200'}`}>
        {leading}
        <span className={`text-sm font-semibold ${isError ? 'text-error-dark' : 'text-surface-800'}`}>{title}</span>
        {subtitle && (
          <span className="text-[11px] text-surface-400 ml-1">{subtitle}</span>
        )}
        {toolbar && <div className="ml-auto flex items-center gap-0.5">{toolbar}</div>}
      </div>
      <div className={`bg-surface-0 px-3 py-2.5 flex-1 min-h-0 overflow-auto ${bodyClassName}`}>
        {children}
      </div>
    </section>
  );
}

function CopyDownloadToolbar({
  copyText, copied, onCopy, onDownload,
}) {
  return (
    <>
      {onDownload && (
        <Tooltip title="Download" arrow>
          <IconButton onClick={onDownload} size="small" sx={{ padding: '2px' }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: '14px', color: '#A8A29E' }} />
          </IconButton>
        </Tooltip>
      )}
      {copyText != null && (
        <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
          <IconButton onClick={onCopy} size="small" sx={{ padding: '2px' }}>
            {copied ? (
              <CheckIcon sx={{ fontSize: '12px', color: '#059669' }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: '12px', color: '#A8A29E' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

function CopyableMetaRow({
  label, value, sectionKey, copiedSection, onCopy,
}) {
  const copied = copiedSection === sectionKey;
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] font-medium text-surface-400 uppercase tracking-wider w-28 flex-shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <span className="text-xs font-mono text-surface-700 truncate">
          {value || 'None'}
        </span>
        {value && (
          <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
            <IconButton
              onClick={() => onCopy(value, sectionKey)}
              size="small"
              sx={{ padding: '2px' }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: '11px', color: '#059669' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: '11px', color: '#A8A29E' }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function UpstreamBody({ entry }) {
  if (!entry) return <EmptyBodyInline text="No body" />;
  const { body, request_type: requestType } = entry;
  if (requestType === 'graphql' && body && typeof body === 'object') {
    return (
      <>
        <div className="text-[11px] text-surface-400 mb-1">
          query
          {entry.operation ? ` · ${entry.operation}` : ''}
        </div>
        <pre className="font-mono text-[13px] text-surface-700 whitespace-pre overflow-x-auto bg-surface-50 rounded px-2 py-1.5 border border-surface-200">
          {body.query || 'None'}
        </pre>
        {body.variables != null && (
          <>
            <div className="text-[11px] text-surface-400 mt-2 mb-1">variables</div>
            <JsonSyntaxHighlighter
              data={body.variables}
              filterHidden={false}
              defaultExpandDepth={Infinity}
            />
          </>
        )}
      </>
    );
  }
  if (requestType === 'xml' && typeof body === 'string') {
    return (
      <pre className="font-mono text-[13px] text-surface-700 whitespace-pre overflow-x-auto">{body}</pre>
    );
  }
  if (body && typeof body === 'object') {
    return (
      <JsonSyntaxHighlighter
        data={body}
        filterHidden={false}
        defaultExpandDepth={Infinity}
      />
    );
  }
  if (body == null) return <EmptyBodyInline text="No body" />;
  return (
    <pre className="font-mono text-[13px] text-surface-700 whitespace-pre-wrap break-words">
      {String(body)}
    </pre>
  );
}

function ApiLogDetailDrawer({
  showApiLogDetail,
  apiLogDetail,
  closeDrawer,
  getIntegrationDisplayName,
  requestPathToTitleMap,
  setStatusCodeFilter,
  setRequestMethodFilter,
}) {
  const [endpointReadable, setEndpointReadable] = useState('');
  const [fullUrl, setFullUrl] = useState('');
  const [logo, setLogo] = useState('ts_logo_2024.png');
  const [copiedSection, setCopiedSection] = useState(null);
  const [activeRequestTab, setActiveRequestTab] = useState(0);
  const [activeMainTab, setActiveMainTab] = useState('query');
  const [downloadMenu, setDownloadMenu] = useState({
    open: false, section: null, x: 0, y: 0,
  });

  const openDownloadMenu = (event, section) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDownloadMenu({
      open: true,
      section,
      x: rect.right,
      y: rect.bottom + 4,
    });
  };
  const closeDownloadMenu = () => setDownloadMenu({
    open: false, section: null, x: 0, y: 0,
  });

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const downloadTextFile = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const sanitizeFilenamePart = (value) => {
    if (!value) return '';
    return String(value).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  };

  const getDisplayNames = () => {
    const requestPath = apiLogDetail.request_path;
    let fullPath = requestPath;
    if (apiLogDetail.path_param) {
      fullPath = fullPath.replace(/<[^>]+>/, apiLogDetail.path_param);
    }
    // if the request path is /install/<path_param>/<path_param>
    // or /integrations/<path_param>/<path_param>, remove the second <path_param>.
    // We don't include wms/cart in the api log
    if ((fullPath.includes('install') || fullPath.includes('integrations')) && fullPath.includes('<path_param>')) {
      fullPath = fullPath.replace('/<path_param>', '');
    }
    let readableEndpoint = '';
    // add integration type to display name
    if (!fullPath.includes('install') && !fullPath.includes('integrations')) {
      readableEndpoint = `${apiLogDetail.request_path.slice(1).split('/')[0].toUpperCase()} | `;
    }
    readableEndpoint += requestPathToTitleMap?.[apiLogDetail.request_path]?.[apiLogDetail.request_method] ?? 'Loading...';
    setEndpointReadable(readableEndpoint);
    setFullUrl(fullPath);
  };

  useEffect(() => {
    if (!apiLogDetail) {
      return;
    }
    getDisplayNames();
    if (apiLogDetail.integration_name !== 'sandbox') {
      setLogo(`https://trackstarlogosbucket.s3.amazonaws.com/${apiLogDetail.integration_name}.png`);
    } else {
      setLogo('ts_logo_2024.png');
    }
  }, [apiLogDetail, requestPathToTitleMap]);

  useEffect(() => {
    setActiveRequestTab(0);
    const method = apiLogDetail?.request_method;
    const defaultTab = method === 'GET' || method === 'DELETE' ? 'query' : 'request';
    setActiveMainTab(defaultTab);
  }, [apiLogDetail?.id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // status badge styling
  const getStatusBadgeClass = (code) => {
    if (code === 200) return 'badge badge-success';
    if (code === 429) return 'badge badge-warning';
    return 'badge badge-error';
  };

  const getStatusAccentColor = (code) => {
    if (code >= 200 && code < 300) return '#10B981';
    if (code === 429) return '#F59E0B';
    if (code >= 400) return '#EF4444';
    return '#A8A29E';
  };

  const normalizeMultilineText = (value) => {
    if (typeof value !== 'string') {
      return value;
    }
    const lines = value.replace(/\r\n/g, '\n').split('\n');
    while (lines.length > 0 && lines[0].trim() === '') {
      lines.shift();
    }
    while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
      lines.pop();
    }
    const nonEmpty = lines.filter((line) => line.trim() !== '');
    if (nonEmpty.length === 0) {
      return '';
    }
    const minIndent = Math.min(...nonEmpty.map((line) => line.match(/^ */)[0].length));
    return lines.map((line) => line.slice(minIndent)).join('\n');
  };

  const countBracketDelta = (line) => {
    let inQuote = false;
    let quoteChar = '';
    let escaped = false;
    let delta = 0;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (escaped) {
        escaped = false;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (inQuote) {
        if (ch === quoteChar) {
          inQuote = false;
          quoteChar = '';
        }
        // eslint-disable-next-line no-continue
        continue;
      }
      if (ch === '"' || ch === '\'') {
        inQuote = true;
        quoteChar = ch;
        // eslint-disable-next-line no-continue
        continue;
      }
      if (ch === '{' || ch === '(' || ch === '[') {
        delta += 1;
      } else if (ch === '}' || ch === ')' || ch === ']') {
        delta -= 1;
      }
    }
    return delta;
  };

  const countLeadingClosers = (line) => {
    let count = 0;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '}' || ch === ')' || ch === ']') {
        count += 1;
      } else {
        break;
      }
    }
    return count;
  };

  const formatGraphqlQuery = (value) => {
    const normalized = normalizeMultilineText(value);
    if (typeof normalized !== 'string' || normalized.trim() === '') {
      return normalized;
    }
    const lines = normalized.split('\n');
    let indentLevel = 0;
    const formatted = lines.map((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return '';
      }
      const leadingClosers = countLeadingClosers(trimmed);
      const lineIndent = Math.max(0, indentLevel - leadingClosers);
      const output = `${'  '.repeat(lineIndent)}${trimmed}`;
      indentLevel = Math.max(0, indentLevel + countBracketDelta(trimmed));
      return output;
    });
    return formatted.join('\n');
  };

  const graphqlOperationName = (query) => {
    if (typeof query !== 'string') {
      return null;
    }
    const match = query.match(/\b(query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)?/);
    if (!match) {
      return null;
    }
    return match[2] || `${match[1]} (anonymous)`;
  };

  const parseJsonStringBody = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  };

  const looksLikeXml = (value) => {
    if (typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    return trimmed.startsWith('<') && trimmed.endsWith('>');
  };

  const prettyXml = (xml) => {
    const raw = xml.replace(/>\s*</g, '><').trim();
    const nodes = raw.split(/>\s*</).map((node, idx, arr) => {
      if (arr.length === 1) return node;
      if (idx === 0) return `${node}>`;
      if (idx === arr.length - 1) return `<${node}`;
      return `<${node}>`;
    });
    const lines = [];
    let indent = 0;
    nodes.forEach((node) => {
      const isClosing = /^<\//.test(node);
      const isSelfClosing = /\/>$/.test(node) || /^<\?/.test(node) || /^<!/.test(node);
      if (isClosing) {
        indent = Math.max(indent - 1, 0);
      }
      lines.push(`${'  '.repeat(indent)}${node}`);
      const isOpening = /^<[^!?/][^>]*>$/.test(node);
      if (isOpening && !isSelfClosing && !isClosing) {
        indent += 1;
      }
    });
    return lines.join('\n');
  };

  const parseFormEncoded = (value) => {
    if (typeof value !== 'string' || !value.includes('=')) {
      return null;
    }
    try {
      const params = new URLSearchParams(value);
      const keys = Array.from(params.keys());
      if (keys.length === 0) {
        return null;
      }
      const data = {};
      keys.forEach((key) => {
        const values = params.getAll(key);
        data[key] = values.length > 1 ? values : values[0];
      });
      return data;
    } catch {
      return null;
    }
  };

  const formatIntegrationRequestEntry = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return entry;
    }
    const { body } = entry;
    const url = entry.url || '';
    if (body && typeof body === 'object' && typeof body.query === 'string') {
      return {
        ...entry,
        request_type: 'graphql',
        operation: graphqlOperationName(body.query),
        body: {
          query: formatGraphqlQuery(body.query),
          ...(body.variables != null ? { variables: body.variables } : {}),
          ...(body.operationName ? { operationName: body.operationName } : {}),
        },
      };
    }
    if (
      typeof body === 'string'
      && (
        url.toLowerCase().includes('graphql')
        || /^\s*(query|mutation|subscription)\b/.test(body)
      )
    ) {
      return {
        ...entry,
        request_type: 'graphql',
        operation: graphqlOperationName(body),
        body: { query: formatGraphqlQuery(body) },
      };
    }
    if (typeof body === 'string') {
      const parsedJson = parseJsonStringBody(body);
      if (parsedJson != null) {
        return {
          ...entry,
          request_type: 'json-string',
          body: parsedJson,
        };
      }
      if (looksLikeXml(body)) {
        return {
          ...entry,
          request_type: 'xml',
          body: prettyXml(body),
        };
      }
      const form = parseFormEncoded(body);
      if (form != null) {
        return {
          ...entry,
          request_type: 'form-encoded',
          body: form,
        };
      }
    }
    return entry;
  };

  const formattedIntegrationRequestBodies = Array.isArray(apiLogDetail?.integration_request_bodies)
    ? apiLogDetail.integration_request_bodies.map(formatIntegrationRequestEntry)
    : null;

  const renderJson = (value) => JSON.stringify(value, null, 2);

  const copyableEntryText = (entry) => {
    if (!entry) return 'None';
    const { body } = entry;
    const isGraphql = entry.request_type === 'graphql' && body && typeof body === 'object';
    if (isGraphql) {
      const parts = [];
      if (body.query) {
        parts.push(`GraphQL Query:\n${body.query}`);
      }
      if (body.variables != null) {
        parts.push(`Variables:\n${renderJson(body.variables)}`);
      }
      return parts.join('\n\n');
    }
    if (body == null) return 'None';
    if (typeof body === 'string') return body;
    return renderJson(body);
  };

  const downloadableEntryText = (entry) => {
    if (!entry) return 'None';
    const header = `${entry.method || 'N/A'} ${entry.url || 'N/A'}`;
    return `${header}\n\n${copyableEntryText(entry)}`;
  };

  const rawIntegrationEntryJson = (idx) => {
    const rawEntry = apiLogDetail?.integration_request_bodies?.[idx];
    if (!rawEntry) return 'null';
    return JSON.stringify(rawEntry, null, 2);
  };

  const allIntegrationEntriesJson = () => {
    const raw = apiLogDetail?.integration_request_bodies;
    if (!Array.isArray(raw) || raw.length === 0) return '[]';
    return JSON.stringify(raw, null, 2);
  };

  const allIntegrationEntriesText = () => {
    if (!Array.isArray(formattedIntegrationRequestBodies)
      || formattedIntegrationRequestBodies.length === 0) {
      return 'None';
    }
    return formattedIntegrationRequestBodies.map((entry, idx) => {
      const divider = `===== Request ${idx + 1} of ${formattedIntegrationRequestBodies.length} =====`;
      return `${divider}\n${downloadableEntryText(entry)}`;
    }).join('\n\n');
  };

  const allIntegrationRequestsFilename = (ext) => {
    const parts = [
      sanitizeFilenamePart(apiLogDetail?.integration_name) || 'integration',
      'all_requests',
      sanitizeFilenamePart(apiLogDetail?.id) || 'log',
    ].filter(Boolean);
    return `${parts.join('_')}.${ext}`;
  };

  const integrationRequestFilename = (entry, idx, ext) => {
    const parts = [
      sanitizeFilenamePart(apiLogDetail?.integration_name) || 'integration',
      sanitizeFilenamePart(entry?.method),
      sanitizeFilenamePart(apiLogDetail?.id) || `request_${idx}`,
    ].filter(Boolean);
    return `${parts.join('_')}.${ext}`;
  };

  const trackstarRequestFilename = (ext) => {
    const parts = [
      'trackstar',
      sanitizeFilenamePart(apiLogDetail?.request_method),
      sanitizeFilenamePart(apiLogDetail?.id) || 'request',
    ].filter(Boolean);
    return `${parts.join('_')}.${ext}`;
  };

  const trackstarRequestDownloadText = () => {
    const method = apiLogDetail?.request_method || 'N/A';
    const path = fullUrl || apiLogDetail?.request_path || '';
    const body = apiLogDetail?.request_body
      ? JSON.stringify(apiLogDetail.request_body, null, 2)
      : 'None';
    return `${method} ${path}\n\n${body}`;
  };

  const trackstarRequestDownloadJson = () => {
    if (apiLogDetail?.request_body == null) return 'null';
    return JSON.stringify(apiLogDetail.request_body, null, 2);
  };

  const formatDuration = (ms) => {
    if (ms == null) return null;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
  };

  const hasQueryParams = apiLogDetail?.query_params
    && Object.keys(apiLogDetail.query_params).length > 0;
  const hasUpstream = Array.isArray(formattedIntegrationRequestBodies)
    && formattedIntegrationRequestBodies.length > 0;
  const hasError = apiLogDetail?.error && apiLogDetail.error !== 'null';
  const activeEntry = hasUpstream ? formattedIntegrationRequestBodies[activeRequestTab] : null;
  const integrationName = apiLogDetail?.integration_name || 'Integration';
  const capitalizedIntegrationName = (
    integrationName.charAt(0).toUpperCase() + integrationName.slice(1)
  );
  const integrationTitle = `${capitalizedIntegrationName} Requests`;
  // prefer the human-readable display name; getIntegrationDisplayName can return
  // a loader element while integration lists load, so fall back to the name slug
  const resolvedDisplayName = getIntegrationDisplayName(
    apiLogDetail?.integration_type,
    apiLogDetail?.integration_name,
  );
  const integrationDisplayName = typeof resolvedDisplayName === 'string' && resolvedDisplayName
    ? resolvedDisplayName
    : capitalizedIntegrationName;

  return (
    <div className="flex flex-col h-full bg-surface-0">
      {showApiLogDetail && apiLogDetail ? (
        <>
          {/* Header — status-accent top border, URL is primary */}
          <div
            className="sticky top-0 z-10 bg-surface-0/95 backdrop-blur-md border-b border-surface-200 px-6 py-4"
            style={{ borderTop: `3px solid ${getStatusAccentColor(apiLogDetail.status_code)}` }}
          >
            <div className="flex items-start gap-3">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
                <Tooltip title="Filter by method" arrow placement="bottom">
                  <button
                    type="button"
                    className={`request-${apiLogDetail.request_method} w-14 text-center flex-shrink-0 cursor-pointer hover:opacity-80`}
                    onClick={() => setRequestMethodFilter(apiLogDetail.request_method)}
                  >
                    {apiLogDetail.request_method}
                  </button>
                </Tooltip>
                <span className="font-mono text-sm text-surface-800 break-all">
                  {fullUrl}
                </span>
                <span className="text-surface-300">→</span>
                <Tooltip title="Filter by status" arrow placement="bottom">
                  <button
                    type="button"
                    className={`${getStatusBadgeClass(apiLogDetail.status_code)} cursor-pointer hover:opacity-80`}
                    onClick={() => setStatusCodeFilter(String(apiLogDetail.status_code))}
                  >
                    {apiLogDetail.status_code}
                    {getStatusReasonPhrase(apiLogDetail.status_code) && (
                      <span className="ml-1 opacity-80">{getStatusReasonPhrase(apiLogDetail.status_code)}</span>
                    )}
                  </button>
                </Tooltip>
              </div>
              <IconButton
                onClick={closeDrawer}
                size="small"
                className="flex-shrink-0 hover:bg-surface-100"
                sx={{ padding: '4px' }}
              >
                <CloseIcon sx={{ fontSize: '18px', color: '#78716C' }} />
              </IconButton>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-surface-400">
              {endpointReadable && <span>{endpointReadable}</span>}
              {endpointReadable && <span>·</span>}
              <span>{formatDate(apiLogDetail.request_time)}</span>
              {formatDuration(apiLogDetail.duration_ms) && (
                <>
                  <span>·</span>
                  <span>{formatDuration(apiLogDetail.duration_ms)}</span>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 py-5 gap-5">

            <div className="space-y-1.5">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-medium text-surface-400 uppercase tracking-wider w-28 flex-shrink-0">Integration</span>
                {apiLogDetail.integration_name ? (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <img src={logo} alt="" className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="text-xs text-surface-700 truncate">
                      {getIntegrationDisplayName(
                        apiLogDetail.integration_type,
                        apiLogDetail.integration_name,
                      )}
                    </span>
                  </span>
                ) : (
                  <span className="text-xs text-surface-400">None</span>
                )}
              </div>
              <CopyableMetaRow
                label="Connection ID"
                value={apiLogDetail.connection_id}
                sectionKey="connection_id"
                copiedSection={copiedSection}
                onCopy={copyToClipboard}
              />
              <CopyableMetaRow
                label="Request ID"
                value={apiLogDetail.request_id}
                sectionKey="request_id"
                copiedSection={copiedSection}
                onCopy={copyToClipboard}
              />
              {apiLogDetail.idempotency_key && (
                <CopyableMetaRow
                  label="Idempotency Key"
                  value={apiLogDetail.idempotency_key}
                  sectionKey="idempotency_key"
                  copiedSection={copiedSection}
                  onCopy={copyToClipboard}
                />
              )}
            </div>

            {/* Tab bar */}
            <div className="flex gap-0 border-b border-surface-200">
              {[
                {
                  key: 'query',
                  label: 'Query Params',
                  indicator: hasQueryParams
                    ? String(Object.keys(apiLogDetail.query_params).length)
                    : 'empty',
                },
                {
                  key: 'request',
                  label: 'Request Body',
                  indicator: apiLogDetail.request_body ? null : 'empty',
                },
                {
                  key: 'upstream',
                  label: integrationTitle,
                  indicator: hasUpstream
                    ? String(formattedIntegrationRequestBodies.length)
                    : 'empty',
                },
                {
                  key: 'errors',
                  label: 'Errors',
                  indicator: hasError ? 'error' : 'empty',
                },
              ].map(({ key, label, indicator }) => {
                const active = activeMainTab === key;
                const isErrorInd = indicator === 'error';
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveMainTab(key)}
                    style={{ height: '36px' }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${
                      active
                        ? 'border-surface-800 text-surface-800'
                        : 'border-transparent text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    <span>{label}</span>
                    {indicator && indicator !== 'empty' && (() => {
                      let indClass = 'bg-surface-100 text-surface-500';
                      if (isErrorInd) indClass = 'bg-error-light text-error-dark';
                      else if (active) indClass = 'bg-surface-200 text-surface-700';
                      return (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${indClass}`}>
                          {isErrorInd ? '!' : indicator}
                        </span>
                      );
                    })()}
                  </button>
                );
              })}
            </div>

            {/* Active tab content */}
            {activeMainTab === 'request' && (
              <CodePanel
                title="Request Body"
                toolbar={(
                  <CopyDownloadToolbar
                    copyText={apiLogDetail.request_body
                      ? renderJson(apiLogDetail.request_body) : null}
                    copied={copiedSection === 'request_body'}
                    onCopy={() => copyToClipboard(renderJson(apiLogDetail.request_body), 'request_body')}
                    onDownload={apiLogDetail.request_body
                      ? (e) => openDownloadMenu(e, 'request_body')
                      : null}
                  />
                )}
              >
                {apiLogDetail.request_body ? (
                  <JsonSyntaxHighlighter
                    data={apiLogDetail.request_body}
                    filterHidden={false}
                    defaultExpandDepth={Infinity}
                  />
                ) : (
                  <EmptyBodyInline text="No request body" />
                )}
              </CodePanel>
            )}

            {activeMainTab === 'query' && (
              <CodePanel
                title="Query Params"
                toolbar={hasQueryParams ? (
                  <CopyDownloadToolbar
                    copyText={renderJson(apiLogDetail.query_params)}
                    copied={copiedSection === 'query_params'}
                    onCopy={() => copyToClipboard(renderJson(apiLogDetail.query_params), 'query_params')}
                  />
                ) : null}
              >
                {hasQueryParams ? (
                  <JsonSyntaxHighlighter
                    data={apiLogDetail.query_params}
                    filterHidden={false}
                    defaultExpandDepth={Infinity}
                  />
                ) : (
                  <EmptyBodyInline text="No query params" />
                )}
              </CodePanel>
            )}

            {activeMainTab === 'upstream' && !hasUpstream && (
              <CodePanel title={integrationTitle}>
                <SecurityRedactedBody integrationName={integrationDisplayName} />
              </CodePanel>
            )}

            {activeMainTab === 'upstream' && hasUpstream
              && formattedIntegrationRequestBodies.length === 1 && (
              <CodePanel
                title={integrationTitle}
                subtitle={(
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`request-${activeEntry.method || 'N/A'} !text-[10px] !px-1.5 !py-0`}>
                      {activeEntry.method || 'N/A'}
                    </span>
                    <span className="font-mono text-[11px] text-surface-500 truncate max-w-[260px]">
                      {activeEntry.url || 'N/A'}
                    </span>
                  </span>
                )}
                toolbar={(
                  <CopyDownloadToolbar
                    copyText={copyableEntryText(activeEntry)}
                    copied={copiedSection === 'integration_body'}
                    onCopy={() => copyToClipboard(copyableEntryText(activeEntry), 'integration_body')}
                    onDownload={(e) => openDownloadMenu(e, 'integration_body')}
                  />
                )}
              >
                <UpstreamBody entry={activeEntry} />
              </CodePanel>
            )}

            {activeMainTab === 'upstream' && hasUpstream
              && formattedIntegrationRequestBodies.length > 1 && (
              <CodePanel
                title={integrationTitle}
                subtitle={`${formattedIntegrationRequestBodies.length} requests`}
                toolbar={(
                  <Tooltip title="Download all requests" arrow>
                    <button
                      type="button"
                      onClick={(e) => openDownloadMenu(e, 'integration_all')}
                      className="flex items-center gap-1 text-[11px] text-surface-500 hover:text-surface-700 hover:bg-surface-100 px-1.5 py-0.5 rounded"
                    >
                      <FileDownloadOutlinedIcon sx={{ fontSize: '14px' }} />
                      <span>Download all</span>
                    </button>
                  </Tooltip>
                )}
                bodyClassName="!p-0"
              >
                <div className="flex gap-1 border-b border-surface-200 overflow-x-auto px-4 py-1.5">
                  {formattedIntegrationRequestBodies.map((entry, idx) => {
                    const method = entry.method || 'N/A';
                    return (
                      <button
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${method}::${entry.url || ''}::${idx}`}
                        type="button"
                        onClick={() => setActiveRequestTab(idx)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-[11px] whitespace-nowrap border-b-2 transition-colors ${
                          activeRequestTab === idx
                            ? 'border-surface-800 text-surface-800'
                            : 'border-transparent text-surface-400 hover:text-surface-600'
                        }`}
                      >
                        <span className={`request-${method} !text-[10px] !px-1.5 !py-0`}>{method}</span>
                        <span className="font-mono">
                          #
                          {idx + 1}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className={`request-${activeEntry?.method || 'N/A'} !text-[10px] !px-1.5 !py-0`}>
                      {activeEntry?.method || 'N/A'}
                    </span>
                    <span className="font-mono text-[11px] text-surface-600 break-all">
                      {activeEntry?.url || 'N/A'}
                    </span>
                    <div className="ml-auto flex items-center gap-0.5">
                      <CopyDownloadToolbar
                        copyText={copyableEntryText(activeEntry)}
                        copied={copiedSection === 'integration_body'}
                        onCopy={() => copyToClipboard(copyableEntryText(activeEntry), 'integration_body')}
                        onDownload={(e) => openDownloadMenu(e, 'integration_body')}
                      />
                    </div>
                  </div>
                  <UpstreamBody entry={activeEntry} />
                </div>
              </CodePanel>
            )}

            {activeMainTab === 'errors' && (
              <CodePanel
                title="Errors"
                accent={hasError ? 'error' : 'neutral'}
                subtitle={hasError && apiLogDetail.error_origin
                  ? `origin: ${apiLogDetail.error_origin.charAt(0).toUpperCase() + apiLogDetail.error_origin.slice(1)}`
                  : null}
                toolbar={hasError ? (
                  <CopyDownloadToolbar
                    copyText={apiLogDetail.error}
                    copied={copiedSection === 'error'}
                    onCopy={() => copyToClipboard(apiLogDetail.error, 'error')}
                  />
                ) : null}
              >
                {hasError ? (() => {
                  const raw = typeof apiLogDetail.error === 'string'
                    ? apiLogDetail.error.trim()
                    : apiLogDetail.error;
                  let parsed = parseJsonStringBody(raw);
                  // errors often arrive as a JSON fragment like `"key": {...}`
                  // (no enclosing braces); wrap in {} and retry
                  if (parsed == null && typeof raw === 'string' && raw.startsWith('"')) {
                    try {
                      parsed = JSON.parse(`{${raw}}`);
                    } catch {
                      parsed = null;
                    }
                  }
                  if (parsed != null) {
                    return (
                      <JsonSyntaxHighlighter
                        data={parsed}
                        filterHidden={false}
                        defaultExpandDepth={Infinity}
                      />
                    );
                  }
                  return (
                    <pre className="font-mono text-[13px] text-error-dark whitespace-pre-wrap break-words">
                      {apiLogDetail.error}
                    </pre>
                  );
                })() : (
                  <EmptyBodyInline text="No errors" />
                )}
              </CodePanel>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-surface-400">
          No API log detail available
        </div>
      )}
      {downloadMenu.open && (
        <>
          <div
            className="fixed inset-0 z-50"
            onClick={closeDownloadMenu}
            onKeyDown={(e) => e.key === 'Escape' && closeDownloadMenu()}
            role="button"
            tabIndex={0}
            aria-label="Close download menu"
          />
          <div
            className="fixed z-50 bg-surface-0 rounded-lg border border-surface-200 shadow-lg py-1 whitespace-nowrap"
            style={{
              left: downloadMenu.x,
              top: downloadMenu.y,
              transform: 'translateX(-100%)',
            }}
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-xs text-surface-700 hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                if (downloadMenu.section === 'integration_body') {
                  const entry = formattedIntegrationRequestBodies?.[activeRequestTab];
                  downloadTextFile(
                    rawIntegrationEntryJson(activeRequestTab),
                    integrationRequestFilename(entry, activeRequestTab, 'json'),
                  );
                } else if (downloadMenu.section === 'integration_all') {
                  downloadTextFile(
                    allIntegrationEntriesJson(),
                    allIntegrationRequestsFilename('json'),
                  );
                } else if (downloadMenu.section === 'request_body') {
                  downloadTextFile(
                    trackstarRequestDownloadJson(),
                    trackstarRequestFilename('json'),
                  );
                }
                closeDownloadMenu();
              }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: '14px', color: '#78716C' }} />
              <span>Download as <span className="font-mono">.json</span> <span className="text-surface-400">(raw)</span></span>
            </button>
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-xs text-surface-700 hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                if (downloadMenu.section === 'integration_body') {
                  const entry = formattedIntegrationRequestBodies?.[activeRequestTab];
                  downloadTextFile(
                    downloadableEntryText(entry),
                    integrationRequestFilename(entry, activeRequestTab, 'txt'),
                  );
                } else if (downloadMenu.section === 'integration_all') {
                  downloadTextFile(
                    allIntegrationEntriesText(),
                    allIntegrationRequestsFilename('txt'),
                  );
                } else if (downloadMenu.section === 'request_body') {
                  downloadTextFile(
                    trackstarRequestDownloadText(),
                    trackstarRequestFilename('txt'),
                  );
                }
                closeDownloadMenu();
              }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: '14px', color: '#78716C' }} />
              <span>Download as <span className="font-mono">.txt</span> <span className="text-surface-400">(prettified)</span></span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
export default ApiLogDetailDrawer;
